import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, Save, X, FileText } from 'lucide-react';
import { api, Account, Department } from '../../lib/api';

interface JournalLine {
  accountId: string;
  departmentId: string;
  partnerId: string;
  debitAmount: number;
  creditAmount: number;
  taxType: string;
  description: string;
}

// よく使う仕訳テンプレート
const journalTemplates = [
  {
    id: 'sales-cash',
    name: '現金売上',
    description: '現金売上',
    lines: [
      { accountCode: '1110', side: 'debit' as const, description: '現金売上' },
      { accountCode: '4110', side: 'credit' as const, description: '現金売上' },
    ],
  },
  {
    id: 'purchase-ap',
    name: '買掛金仕入',
    description: '商品仕入（買掛金）',
    lines: [
      { accountCode: '5110', side: 'debit' as const, description: '商品仕入' },
      { accountCode: '2110', side: 'credit' as const, description: '商品仕入' },
    ],
  },
  {
    id: 'salary',
    name: '給与支払',
    description: '給与支払',
    lines: [
      { accountCode: '6110', side: 'debit' as const, description: '給与' },
      { accountCode: '1110', side: 'credit' as const, description: '給与支払' },
    ],
  },
  {
    id: 'rent',
    name: '家賃支払',
    description: '家賃支払',
    lines: [
      { accountCode: '6210', side: 'debit' as const, description: '地代家賃' },
      { accountCode: '1110', side: 'credit' as const, description: '家賃支払' },
    ],
  },
  {
    id: 'utility',
    name: '水道光熱費',
    description: '水道光熱費支払',
    lines: [
      { accountCode: '6220', side: 'debit' as const, description: '水道光熱費' },
      { accountCode: '1110', side: 'credit' as const, description: '水道光熱費支払' },
    ],
  },
];

export function JournalCreatePage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<JournalLine[]>([
    { accountId: '', departmentId: '', partnerId: '', debitAmount: 0, creditAmount: 0, taxType: '', description: '' },
    { accountId: '', departmentId: '', partnerId: '', debitAmount: 0, creditAmount: 0, taxType: '', description: '' },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ data: Account[] }>('/api/accounts'),
  });

  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<{ data: Department[] }>('/api/departments', { flat: 'true' }),
  });

  const addLine = () => {
    setLines([...lines, { accountId: '', departmentId: '', partnerId: '', debitAmount: 0, creditAmount: 0, taxType: '', description: '' }]);
  };

  const removeLine = (index: number) => {
    if (lines.length > 2) {
      setLines(lines.filter((_, i) => i !== index));
    }
  };

  const updateLine = (index: number, field: keyof JournalLine, value: any) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  const totalDebit = lines.reduce((sum, line) => sum + (line.debitAmount || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (line.creditAmount || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const applyTemplate = (templateId: string) => {
    const template = journalTemplates.find(t => t.id === templateId);
    if (!template || !accounts?.data) return;

    setDescription(template.description);
    const newLines = template.lines.map(tl => {
      const account = accounts.data.find(a => a.code === tl.accountCode);
      return {
        accountId: account?.id || '',
        departmentId: '',
        partnerId: '',
        debitAmount: tl.side === 'debit' ? 0 : 0,
        creditAmount: tl.side === 'credit' ? 0 : 0,
        taxType: '',
        description: tl.description,
      };
    });
    setLines(newLines);
    setShowTemplates(false);
  };

  const handleSubmit = async () => {
    if (!isBalanced) {
      setError('借方と貸方の合計が一致しません');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/api/journals', {
        journalDate: date,
        journalType: 'normal',
        description,
        lines: lines.filter(l => l.accountId),
      });
      navigate('/journals');
    } catch (err: any) {
      setError(err.message || '仕訳の作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          仕訳入力
        </h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="px-4 py-2 rounded-lg border text-sm"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              <FileText size={16} className="inline mr-1" />
              テンプレート
            </button>
            {showTemplates && (
              <div 
                className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg z-50 py-2"
                style={{ backgroundColor: 'var(--background-card)' }}
              >
                {journalTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template.id)}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <div className="font-medium">{template.name}</div>
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {template.description}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => navigate('/journals')}
            className="px-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            <X size={16} className="inline mr-1" />
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !isBalanced}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            <Save size={16} className="inline mr-1" />
            保存
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              日付
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              摘要
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="取引の概要を入力..."
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>
              <th style={{ color: 'var(--text-primary)' }}>勘定科目</th>
              <th style={{ color: 'var(--text-primary)' }}>部門</th>
              <th style={{ color: 'var(--text-primary)' }}>借方金額</th>
              <th style={{ color: 'var(--text-primary)' }}>貸方金額</th>
              <th style={{ color: 'var(--text-primary)' }}>摘要</th>
              <th style={{ color: 'var(--text-primary)' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index}>
                <td>
                  <select
                    value={line.accountId}
                    onChange={(e) => updateLine(index, 'accountId', e.target.value)}
                    className="w-full px-2 py-1.5 rounded border text-sm"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      borderColor: 'var(--input-border)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">選択...</option>
                    {accounts?.data.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.code} {acc.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={line.departmentId}
                    onChange={(e) => updateLine(index, 'departmentId', e.target.value)}
                    className="w-full px-2 py-1.5 rounded border text-sm"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      borderColor: 'var(--input-border)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">-</option>
                    {departments?.data.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    value={line.debitAmount || ''}
                    onChange={(e) => updateLine(index, 'debitAmount', parseFloat(e.target.value) || 0)}
                    className="w-full px-2 py-1.5 rounded border text-sm text-right font-mono"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      borderColor: 'var(--input-border)',
                      color: 'var(--text-primary)',
                    }}
                    placeholder="0"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={line.creditAmount || ''}
                    onChange={(e) => updateLine(index, 'creditAmount', parseFloat(e.target.value) || 0)}
                    className="w-full px-2 py-1.5 rounded border text-sm text-right font-mono"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      borderColor: 'var(--input-border)',
                      color: 'var(--text-primary)',
                    }}
                    placeholder="0"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={line.description}
                    onChange={(e) => updateLine(index, 'description', e.target.value)}
                    className="w-full px-2 py-1.5 rounded border text-sm"
                    style={{
                      backgroundColor: 'var(--input-bg)',
                      borderColor: 'var(--input-border)',
                      color: 'var(--text-primary)',
                    }}
                    placeholder="行摘要"
                  />
                </td>
                <td>
                  <button
                    onClick={() => removeLine(index)}
                    disabled={lines.length <= 2}
                    className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 disabled:opacity-30"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {/* Totals row */}
            <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
              <td colSpan={2} className="text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                合計
              </td>
              <td className="text-right font-mono font-bold" style={{ color: 'var(--text-primary)' }}>
                ¥{totalDebit.toLocaleString()}
              </td>
              <td className="text-right font-mono font-bold" style={{ color: 'var(--text-primary)' }}>
                ¥{totalCredit.toLocaleString()}
              </td>
              <td colSpan={2}>
                {!isBalanced && (
                  <span className="text-red-500 text-sm">
                    差額: ¥{Math.abs(totalDebit - totalCredit).toLocaleString()}
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <button
            onClick={addLine}
            className="flex items-center gap-1 text-sm"
            style={{ color: 'var(--accent-color)' }}
          >
            <Plus size={16} />
            行を追加
          </button>
        </div>
      </div>
    </div>
  );
}

